import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import {
  normalizeSignificanceSettings,
  SignificanceSettings,
} from "types/significance.types"
import {
  DEFAULT_TABLE_STYLE_CHOICE,
  normalizeTableStyleChoice,
  TableStyleChoice,
} from "types/tableStyle.types"

/** The version of a document without shown labels. */
export const TAB_SPEC_SCHEMA_VERSION = 1 as const
/**
 * The version of a document that holds shown labels: a client that does not
 * know them refuses it as "newer-version" instead of dropping them at its
 * next save.
 */
export const TAB_SPEC_LABELS_SCHEMA_VERSION = 2 as const
/**
 * The version of a document that uses a field that came with version 3
 * (`usesV3`): a client that does not know them refuses it as "newer-version"
 * instead of dropping them at its next save.
 */
export const TAB_SPEC_V3_SCHEMA_VERSION = 3 as const
export type TabSpecSchemaVersion =
  | typeof TAB_SPEC_SCHEMA_VERSION
  | typeof TAB_SPEC_LABELS_SCHEMA_VERSION
  | typeof TAB_SPEC_V3_SCHEMA_VERSION

export type BaseRule = "asked" | "answered" | "all"
export const BASE_RULES: BaseRule[] = ["asked", "answered", "all"]

export type TableKind =
  | "categorical"
  | "multi"
  | "scale"
  | "numeric"
  | "grid-summary"
  | "text"
export const TABLE_KINDS: TableKind[] = [
  "categorical",
  "multi",
  "scale",
  "numeric",
  "grid-summary",
  "text",
]

export type BoxSize = 0 | 2 | 3
export type GridSummary = "top-box" | "mean" | "none"

/**
 * What each grid row (or loop iteration) of a grid summary shows: the share
 * in a top or bottom box, in the middle (the scored answers in neither the
 * spec's top nor its bottom box), giving one answer, or in a named net of
 * answers; the mean; NPS (promoters minus detractors, 0 to 10 scales only);
 * or the sum of a number (number grids and looped numbers only).
 */
export type SummaryChoice =
  | { kind: "top" | "bottom"; size: 1 | 2 | 3 }
  | { kind: "middle" }
  | { kind: "answer"; label: string }
  | { kind: "net"; name: string; members: string[] }
  | { kind: "mean" }
  | { kind: "nps" }
  | { kind: "sum" }

/**
 * How a grid of the data whose rows are sibling containers shows as one
 * table: "stacked", a block of rows per sibling; "row", one row per sibling.
 */
export type SiblingLayout = "stacked" | "row"
export const SIBLING_LAYOUTS: SiblingLayout[] = ["stacked", "row"]

/**
 * A second mean that leaves out the respondents whose value is 0, with its
 * own letters. No statistic of the engine: the script works it out of a
 * field without the zeros.
 */
export const MEAN_EXCLUDING_ZERO = "mean-excl-0"
/** The statistics under a mean that the engine works out: every one but the mean excluding 0. */
export type MeanStatistic =
  | STATISTIC_TYPE.MEDIAN
  | STATISTIC_TYPE.STDDEV
  | STATISTIC_TYPE.STDERR
/** What can be shown under a mean. */
export type MeanStat = MeanStatistic | typeof MEAN_EXCLUDING_ZERO
/** Everything that can be shown under a mean, in the order the tables show them. */
export const MEAN_STAT_TYPES: MeanStat[] = [
  MEAN_EXCLUDING_ZERO,
  STATISTIC_TYPE.MEDIAN,
  STATISTIC_TYPE.STDDEV,
  STATISTIC_TYPE.STDERR,
]
export const isMeanStat = (s: unknown): s is MeanStat =>
  (MEAN_STAT_TYPES as unknown[]).includes(s)
export const isMeanStatistic = (s: unknown): s is MeanStatistic =>
  s !== MEAN_EXCLUDING_ZERO && isMeanStat(s)

/**
 * The order of a table's answer rows (and a grid summary's rows): "none", as
 * the table lists them; "desc", by the Totals column, highest first, within
 * the runs between nets and statistic rows. Exceptions and answers kept at
 * the end (`anchor`) stay at the end of their run.
 */
export type SortRule = "none" | "desc"
export const SORT_RULES: SortRule[] = ["none", "desc"]

export interface TabSpecDefaults {
  bannerId: string
  base: BaseRule
  /** undefined = significance testing off. */
  significance: SignificanceSettings | undefined
  tableStyle: TableStyleChoice
  statistics: { categorical: STATISTIC_TYPE[]; numeric: STATISTIC_TYPE[] }
  scale: { topBox: BoxSize; bottomBox: BoxSize; mean: boolean }
  /**
   * `summary`: the one summary of a grid in the tab spec's first form, which
   * a summary table without its own `summary` still follows. `summaries`:
   * the summaries a grid added from now on gets, each a table of its own
   * (absent: the one `summary` gives, as before).
   */
  grid: { summary: GridSummary; perRow: boolean; summaries?: SummaryChoice[] }
  includeHidden: boolean
  /** Shown under every mean (and after a numeric table's statistics). Absent = none. */
  meanStats?: MeanStat[]
  /** The order of the answer rows of every table. Absent = "none". */
  sort?: SortRule
  /** Answer rows nobody gave are left out instead of shown with zeros. Absent = false. */
  hideEmpty?: boolean
}

/**
 * A column of a banner number variable: the respondents whose value lies
 * between `min` and `max`, both inclusive. At least one bound; one left out
 * is open.
 */
export interface BannerRange {
  kind: "range"
  name: string
  min?: number
  max?: number
}

/** A column of the respondents for whom `condition` holds. */
export interface BannerCondition {
  kind: "condition"
  name: string
  condition: Condition
}

export type BannerPoint =
  | { kind: "category"; label: string; rename?: string }
  | { kind: "net"; name: string; members: string[] }
  /** Number variables only. */
  | BannerRange
  /** Any group; the only kind in a group of conditions (`variable` ""). */
  | BannerCondition

export interface BannerGroup {
  id: string
  /**
   * Question label. Its only instance supplies the paths, or the one
   * `instance` names. "" for a group of conditions: every point is a
   * condition, and `label` is its heading.
   */
  variable: string
  /** "data": `variable` is the codebook label of a variable of the prepared data. Absent = a questionnaire question. */
  source?: "data"
  /** `CatalogueInstance.id` of the grid row or loop iteration the columns use. Absent = the question's only instance. */
  instance?: string
  label?: string
  /** null = every option, questionnaire order. A number variable's are its ranges. */
  points: BannerPoint[] | null
}

/** A group of condition columns: no variable, a heading of its own. */
export const isConditionGroup = (g: Pick<BannerGroup, "variable">): boolean => g.variable === ""

export interface Banner {
  id: string
  name: string
  groups: BannerGroup[]
}

/**
 * A number in a range: the field at `path` lies between `min` and `max`,
 * both inclusive. A term has at least one bound; one left out is open.
 */
export interface NumberTerm {
  path: string
  min?: number
  max?: number
}
/** A string: the answer at that codebook path, its yes or no leaf true. Or a number in a range. */
export type ConditionTerm = string | NumberTerm
/** Holds when none of its terms holds. */
export interface NoneGroup {
  none: ConditionTerm[]
}
/** A list of terms holds when any of them holds; a "none of" group when none does. */
export type ConditionGroup = ConditionTerm[] | NoneGroup
/**
 * Every group holds; [] = everyone. A stored `string[][]` filter (OR within
 * a group, AND across groups) is a valid condition as it is. A respondent
 * with a missing value is in neither a condition nor its negation.
 */
export type Condition = ConditionGroup[]

export const isNumberTerm = (t: ConditionTerm): t is NumberTerm => typeof t !== "string"
export const isNoneGroup = (g: ConditionGroup): g is NoneGroup => !Array.isArray(g)

/**
 * A range of a number: the respondents whose value lies between `min` and
 * `max`, both inclusive. At least one bound; one left out is open.
 */
export interface RangeRow {
  kind: "range"
  name: string
  min?: number
  max?: number
}

export type RowItem =
  /** `anchor`: kept at the end of its run when the rows are sorted, as an exception is. */
  | { kind: "option"; label: string; hidden?: boolean; rename?: string; anchor?: boolean }
  | { kind: "net"; name: string; members: string[] }
  /** `plus` minus `minus`, both names of nets in the same table. Computed in the client, never tested. */
  | { kind: "difference"; name: string; plus: string; minus: string }
  | { kind: "mean" }
  /** Number tables only: a row of the respondents whose value lies in the range. */
  | RangeRow

export const isRangeRow = (r: RowItem): r is RangeRow => r.kind === "range"

export interface TableSpec {
  id: string
  /** Question label: the identity. */
  question: string
  /** "data": `question` is the codebook label of a variable of the prepared data. Absent = a questionnaire question. */
  source?: "data"
  /** `CatalogueInstance.id`; null for a plain question and for a grid summary. */
  instance: string | null
  kind: TableKind
  origin: "default" | "added"
  include: boolean
  title?: string
  bannerId?: string
  base?: BaseRule
  baseText?: string
  significance?: SignificanceSettings | "off"
  statistics?: STATISTIC_TYPE[]
  /**
   * Shown under the table's mean. Absent = the defaults'; [] = none. A number
   * table with its own `statistics` keeps its other statistics in that list
   * and only the mean excluding 0 here.
   */
  meanStats?: MeanStat[]
  /** The order of the answer rows (a grid summary's rows). Absent = the defaults'. */
  sort?: SortRule
  /** Answer rows nobody gave (a grid summary's rows nobody answered) are left out. Absent = the defaults'. */
  hideEmpty?: boolean
  /** Who the table counts besides its base: every group holds. A stored `string[][]` is one. */
  filter: Condition
  /**
   * null = the defaults for the kind, following the questionnaire. A number
   * table's are its ranges (null: none).
   */
  rows: RowItem[] | null
  /**
   * Number tables only: every value given is a row of its own, above the
   * ranges and the statistics. Written only when true.
   */
  values?: boolean
  /** Option label to score. null = default scores. */
  scores: Record<string, number> | null
  /**
   * Grid summaries only: what each grid row shows. Absent = the summary of
   * the defaults' `grid.summary` (the mean for a number question), as before.
   */
  summary?: SummaryChoice
  /**
   * Grid summaries only: the instance parts the table is fixed at, null
   * where its rows run (`CatalogueInstance.parts`): `[null, "Online"]` is a
   * row per grid row of the column Online. Absent = every instance is a row.
   */
  slice?: (string | null)[]
  /** Grid summaries only: `CatalogueInstance.id` of the rows left out. */
  hiddenRows?: string[]
  /**
   * A grid of the data whose rows are sibling containers
   * (`CatalogueQuestion.siblings`) shown as one table (`instance` null, kind
   * "categorical" or "multi"): each sibling's rows in a block of their own
   * ("stacked", also when absent), or one row per sibling ("row"), the row
   * `siblingRow` of each. Ignored on every other table.
   */
  siblingLayout?: SiblingLayout
  /** The row a "row" layout shows: the name of a net or a difference row (NPS), or an option's raw label. */
  siblingRow?: string
  note?: string
}

export interface Approval {
  state: "draft" | "in-review" | "approved"
  approvedBy?: string
  approvedAt?: string
  approvedHash?: string
}

/**
 * A name the tables show in place of a raw label. The raw label stays the
 * identity: every path, filter and script keeps it. One entry per variable,
 * option or instance; at most one of `option` and `instance`.
 */
export interface ShownLabel {
  /** Label of the question or data variable (`TableSpec.question`, `BannerGroup.variable`). */
  variable: string
  /** Raw label of one of its options (an answer option, or a category from the data). */
  option?: string
  /** `CatalogueInstance.id` of one of its instances: a grid row, a numeric cell, a loop iteration. */
  instance?: string
  shown: string
}

/** What a shown label names: a variable, or one of its options or instances. */
export interface LabelTarget {
  variable: string
  option?: string
  instance?: string
}

export type LabelKind = "variable" | "option" | "instance"

export const kindOf = (t: LabelTarget): LabelKind =>
  t.option !== undefined ? "option" : t.instance !== undefined ? "instance" : "variable"

/** One string per target, for maps; never shown. */
export const labelKey = (t: LabelTarget): string =>
  JSON.stringify([t.variable, kindOf(t), t.option ?? t.instance ?? ""])

/**
 * The target of a shown label, or null when it names nothing a document may
 * hold: `variable` a non-empty string, and at most one of `option` and
 * `instance`, each a non-empty string when present. The one rule of the
 * normalizer and of `applyLabelChanges`.
 */
export const labelTargetOf = (raw: {
  variable?: unknown
  option?: unknown
  instance?: unknown
}): LabelTarget | null => {
  const { variable } = raw
  if (typeof variable !== "string" || !variable) return null
  // undefined: absent; null: present but not a non-empty string (refused).
  const item = (v: unknown): string | null | undefined =>
    v === undefined ? undefined : typeof v === "string" && v ? v : null
  const option = item(raw.option)
  const instance = item(raw.instance)
  if (option === null || instance === null || (option !== undefined && instance !== undefined))
    return null
  return {
    variable,
    ...(option !== undefined ? { option } : {}),
    ...(instance !== undefined ? { instance } : {}),
  }
}

/** What `usesV3` and `schemaVersionFor` read of a document. */
export type VersionedParts = Pick<
  TabSpecDocument,
  "defaults" | "banners" | "tables" | "labels" | "filter"
>

/** A statistic that came with version 3. */
const isV3Statistic = (s: STATISTIC_TYPE): boolean => isMeanStatistic(s) || s === STATISTIC_TYPE.SUM

/** A condition holds a number term or a "none of" group, which came with version 3. */
const isV3Condition = (c: Condition): boolean =>
  c.some((g) => isNoneGroup(g) || g.some(isNumberTerm))

/**
 * Whether the document uses a field, or a value of an older field, that came
 * with version 3. A field that is absent does not count, so a document that
 * uses none of them is written as before. One check per feature: each later
 * feature adds its own line.
 */
export const usesV3 = (doc: VersionedParts): boolean =>
  [
    // Statistics under the mean: the defaults' or a table's own.
    doc.defaults.meanStats !== undefined || doc.tables.some((t) => t.meanStats !== undefined),
    // A statistic of version 3 (the median, the standard deviation, the
    // standard error or the sum) in a list of statistics: an older client
    // knows none of them, so it would drop them at its next save.
    doc.tables.some((t) => t.statistics?.some(isV3Statistic)) ||
      [...doc.defaults.statistics.numeric, ...doc.defaults.statistics.categorical].some(isV3Statistic),
    // Conditions: a book filter, or a number term or a "none of" group in a
    // table's filter. A plain `string[][]` filter is as old as the tab spec.
    (doc.filter?.length ?? 0) > 0 || doc.tables.some((t) => isV3Condition(t.filter)),
    // Number ranges as rows: an older client drops a row it does not know.
    doc.tables.some((t) => t.rows?.some(isRangeRow)),
    // Each value of a number as a row.
    doc.tables.some((t) => t.values === true),
    // Banners: a group on a grid row or loop iteration, range columns,
    // condition columns, a group of conditions with no variable.
    doc.banners.some((b) => b.groups.some((g) => g.instance !== undefined)),
    doc.banners.some((b) => b.groups.some((g) => g.points?.some((p) => p.kind === "range"))),
    doc.banners.some((b) => b.groups.some((g) => g.points?.some((p) => p.kind === "condition"))),
    doc.banners.some((b) => b.groups.some(isConditionGroup)),
    // Grid summaries: what each row shows, the slice, the rows left out, and
    // the summaries new grids get.
    doc.tables.some((t) => t.summary !== undefined),
    doc.tables.some((t) => t.slice !== undefined),
    doc.tables.some((t) => t.hiddenRows !== undefined),
    doc.defaults.grid.summaries !== undefined,
    // Sorting and empty rows: the defaults', a table's own, and an answer
    // kept at the end.
    doc.defaults.sort !== undefined || doc.tables.some((t) => t.sort !== undefined),
    doc.defaults.hideEmpty !== undefined || doc.tables.some((t) => t.hideEmpty !== undefined),
    doc.tables.some((t) => t.rows?.some((r) => r.kind === "option" && r.anchor === true)),
  ].some(Boolean)

/**
 * The version a document is written with: 3 while it uses a field of
 * version 3 (`usesV3`), else 2 while it holds a shown label, else 1.
 */
export const schemaVersionFor = (doc: VersionedParts): TabSpecSchemaVersion =>
  usesV3(doc)
    ? TAB_SPEC_V3_SCHEMA_VERSION
    : doc.labels && doc.labels.length > 0
      ? TAB_SPEC_LABELS_SCHEMA_VERSION
      : TAB_SPEC_SCHEMA_VERSION

export interface TabSpecDocument {
  /** 1, 2 while the document holds shown labels, 3 while it uses a field of version 3 (`schemaVersionFor`). */
  schemaVersion: TabSpecSchemaVersion
  id: string
  name: string
  variant: string | null
  data: { pipelines: string[]; weight: string | null }
  defaults: TabSpecDefaults
  banners: Banner[]
  tables: TableSpec[]
  /**
   * The book filter: a condition every table of the spec counts within, on
   * top of its own base and filter. Written only when it has a group: a
   * document without one has no `filter` key.
   */
  filter?: Condition
  /** Shown labels, written only when there is one. A document without them has no `labels` key. */
  labels?: ShownLabel[]
  approval: Approval
  updatedAt: string
  updatedBy: string
}

export type NormalizeResult =
  | { ok: true; document: TabSpecDocument }
  | { ok: false; reason: "newer-version" | "invalid" }

export const DEFAULT_TAB_SPEC_SIGNIFICANCE: SignificanceSettings = {
  enabled: true,
  levels: [95],
  vsRest: false,
  overlap: true,
  minBase: 30,
}

export const newTabSpecId = (
  prefix: "spec" | "table" | "banner" | "group"
): string =>
  `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

/** `base`: "answered" in a project without a questionnaire, where nobody knows who was asked. */
export const createDefaults = (bannerId: string, base: BaseRule = "asked"): TabSpecDefaults => ({
  bannerId,
  base,
  significance: DEFAULT_TAB_SPEC_SIGNIFICANCE,
  tableStyle: DEFAULT_TABLE_STYLE_CHOICE,
  statistics: {
    categorical: [STATISTIC_TYPE.COL_PERCENT, STATISTIC_TYPE.N],
    numeric: [
      STATISTIC_TYPE.AVG,
      STATISTIC_TYPE.MIN,
      STATISTIC_TYPE.MAX,
      STATISTIC_TYPE.BASE_N,
    ],
  },
  scale: { topBox: 2, bottomBox: 2, mean: true },
  grid: { summary: "top-box", perRow: true },
  includeHidden: true,
})

/** A spec with one empty banner ("Total only") and no tables. `base`: the default base rule. */
export const createEmptySpec = (
  name: string,
  user: string,
  base: BaseRule = "asked"
): TabSpecDocument => {
  const banner: Banner = { id: newTabSpecId("banner"), name: "Main banner", groups: [] }
  return {
    schemaVersion: TAB_SPEC_SCHEMA_VERSION,
    id: newTabSpecId("spec"),
    name,
    variant: null,
    data: { pipelines: [], weight: null },
    defaults: createDefaults(banner.id, base),
    banners: [banner],
    tables: [],
    approval: { state: "draft" },
    updatedAt: new Date().toISOString(),
    updatedBy: user,
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)
const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback)
const optStr = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v : undefined
const strArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []
const oneOf = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T =>
  typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : fallback
const STATS = Object.values(STATISTIC_TYPE) as string[]
const stats = (v: unknown): STATISTIC_TYPE[] =>
  strArray(v).filter((s) => STATS.includes(s)) as STATISTIC_TYPE[]
const box = (v: unknown, fallback: BoxSize): BoxSize =>
  v === 0 || v === 2 || v === 3 ? v : fallback
/** The statistics under the mean of a stored list: the known ones, in stored order, each once. */
const meanStatList = (v: unknown): MeanStat[] => [...new Set(strArray(v).filter(isMeanStat))]
/** A stored sort rule, or undefined for anything else. */
const sortRule = (v: unknown): SortRule | undefined =>
  (SORT_RULES as unknown[]).includes(v) ? (v as SortRule) : undefined

/** A stored bound: a finite number, else absent. */
const bound = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v) ? v : undefined

/** A stored term: a string as it is, or a number term (a path and its finite bounds); null for anything else. */
const normalizeTerm = (raw: unknown): ConditionTerm | null => {
  if (typeof raw === "string") return raw
  if (!isRecord(raw) || typeof raw.path !== "string" || !raw.path) return null
  const min = bound(raw.min)
  const max = bound(raw.max)
  return {
    path: raw.path,
    ...(min !== undefined ? { min } : {}),
    ...(max !== undefined ? { max } : {}),
  }
}

const termList = (v: unknown): ConditionTerm[] =>
  Array.isArray(v)
    ? v.map(normalizeTerm).filter((t): t is ConditionTerm => t !== null)
    : []

/**
 * A stored condition: lists of terms and "none of" groups, each kept with at
 * least one term it can read; anything else is dropped. A stored
 * `string[][]` filter reads back as it was.
 */
const normalizeCondition = (raw: unknown): Condition =>
  Array.isArray(raw)
    ? raw.flatMap((g): ConditionGroup[] => {
        const terms = Array.isArray(g) ? termList(g) : isRecord(g) ? termList(g.none) : []
        if (terms.length === 0) return []
        return [Array.isArray(g) ? terms : { none: terms }]
      })
    : []

const normalizeRowItem = (raw: unknown): RowItem | null => {
  if (!isRecord(raw)) return null
  if (raw.kind === "mean") return { kind: "mean" }
  if (raw.kind === "option" && typeof raw.label === "string" && raw.label)
    return {
      kind: "option",
      label: raw.label,
      ...(raw.hidden === true ? { hidden: true } : {}),
      ...(optStr(raw.rename) ? { rename: optStr(raw.rename) } : {}),
      // Only true is written: a row without it has no `anchor` key.
      ...(raw.anchor === true ? { anchor: true } : {}),
    }
  if (raw.kind === "net" && typeof raw.name === "string" && raw.name)
    return { kind: "net", name: raw.name, members: strArray(raw.members) }
  if (
    raw.kind === "difference" &&
    typeof raw.name === "string" &&
    typeof raw.plus === "string" &&
    typeof raw.minus === "string"
  )
    return { kind: "difference", name: raw.name, plus: raw.plus, minus: raw.minus }
  // A range: a name, and at least one finite bound. Keys as name, min, max.
  if (raw.kind === "range" && typeof raw.name === "string" && raw.name.trim()) {
    const min = bound(raw.min)
    const max = bound(raw.max)
    if (min === undefined && max === undefined) return null
    return {
      kind: "range",
      name: raw.name,
      ...(min !== undefined ? { min } : {}),
      ...(max !== undefined ? { max } : {}),
    }
  }
  return null
}

/**
 * A stored summary choice, keys as the type lists them, or undefined when it
 * cannot be read: an unknown kind, a box size other than 1, 2 or 3, an
 * answer without a label, a net without a name or a member.
 */
export const normalizeSummaryChoice = (raw: unknown): SummaryChoice | undefined => {
  if (!isRecord(raw)) return undefined
  switch (raw.kind) {
    case "top":
    case "bottom":
      return raw.size === 1 || raw.size === 2 || raw.size === 3
        ? { kind: raw.kind, size: raw.size }
        : undefined
    case "middle":
    case "mean":
    case "nps":
    case "sum":
      return { kind: raw.kind }
    case "answer":
      return typeof raw.label === "string" && raw.label ? { kind: "answer", label: raw.label } : undefined
    case "net": {
      const members = [...new Set(strArray(raw.members).filter(Boolean))]
      return typeof raw.name === "string" && raw.name.trim() && members.length > 0
        ? { kind: "net", name: raw.name, members }
        : undefined
    }
    default:
      return undefined
  }
}

/** A stored slice: parts and nulls, at least one null (where the rows run); undefined otherwise. */
const normalizeSlice = (raw: unknown): (string | null)[] | undefined =>
  Array.isArray(raw) &&
  raw.every((p) => p === null || typeof p === "string") &&
  raw.some((p) => p === null)
    ? [...(raw as (string | null)[])]
    : undefined

const normalizeScores = (raw: unknown): Record<string, number> | null => {
  if (!isRecord(raw)) return null
  const out: Record<string, number> = {}
  Object.entries(raw).forEach(([k, v]) => {
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v
  })
  return out
}

const normalizeTable = (raw: unknown): TableSpec | null => {
  if (!isRecord(raw)) return null
  const id = str(raw.id)
  const question = str(raw.question)
  if (!id || !question) return null
  const sig =
    raw.significance === "off" ? "off" : normalizeSignificanceSettings(raw.significance)
  const rows = Array.isArray(raw.rows)
    ? raw.rows.map(normalizeRowItem).filter((r): r is RowItem => r !== null)
    : null
  const base = optStr(raw.base)
  const summary = normalizeSummaryChoice(raw.summary)
  const slice = normalizeSlice(raw.slice)
  return {
    id,
    question,
    ...(raw.source === "data" ? { source: "data" as const } : {}),
    instance: typeof raw.instance === "string" ? raw.instance : null,
    kind: oneOf(raw.kind, TABLE_KINDS, "categorical"),
    origin: raw.origin === "added" ? "added" : "default",
    include: raw.include !== false,
    ...(optStr(raw.title) ? { title: optStr(raw.title) } : {}),
    ...(optStr(raw.bannerId) ? { bannerId: optStr(raw.bannerId) } : {}),
    ...(base && (BASE_RULES as string[]).includes(base) ? { base: base as BaseRule } : {}),
    ...(optStr(raw.baseText) ? { baseText: optStr(raw.baseText) } : {}),
    ...(sig ? { significance: sig } : {}),
    ...(Array.isArray(raw.statistics) ? { statistics: stats(raw.statistics) } : {}),
    // An empty list stays: it says "none", whatever the defaults say.
    ...(Array.isArray(raw.meanStats) ? { meanStats: meanStatList(raw.meanStats) } : {}),
    // Either value overrides the defaults: kept as stored.
    ...(sortRule(raw.sort) ? { sort: sortRule(raw.sort) } : {}),
    ...(typeof raw.hideEmpty === "boolean" ? { hideEmpty: raw.hideEmpty } : {}),
    filter: normalizeCondition(raw.filter),
    rows,
    // Only true is written: a table without it has no `values` key.
    ...(raw.values === true ? { values: true } : {}),
    scores: normalizeScores(raw.scores),
    // Grid summaries: written only when set, so an older table reads back as it was.
    ...(summary ? { summary } : {}),
    ...(slice ? { slice } : {}),
    ...(Array.isArray(raw.hiddenRows) ? { hiddenRows: [...new Set(strArray(raw.hiddenRows))] } : {}),
    ...(SIBLING_LAYOUTS.includes(raw.siblingLayout as SiblingLayout)
      ? { siblingLayout: raw.siblingLayout as SiblingLayout }
      : {}),
    ...(optStr(raw.siblingRow) ? { siblingRow: optStr(raw.siblingRow) } : {}),
    ...(optStr(raw.note) ? { note: optStr(raw.note) } : {}),
  }
}

const normalizePoint = (raw: unknown): BannerPoint | null => {
  if (!isRecord(raw)) return null
  if (raw.kind === "category" && typeof raw.label === "string" && raw.label)
    return {
      kind: "category",
      label: raw.label,
      ...(optStr(raw.rename) ? { rename: optStr(raw.rename) } : {}),
    }
  if (raw.kind === "net" && typeof raw.name === "string" && raw.name)
    return { kind: "net", name: raw.name, members: strArray(raw.members) }
  // A range: a name, and at least one finite bound. Keys as kind, name, min, max.
  if (raw.kind === "range" && typeof raw.name === "string" && raw.name.trim()) {
    const min = bound(raw.min)
    const max = bound(raw.max)
    if (min === undefined && max === undefined) return null
    return {
      kind: "range",
      name: raw.name,
      ...(min !== undefined ? { min } : {}),
      ...(max !== undefined ? { max } : {}),
    }
  }
  // A condition: a name, and the condition as far as it can be read. One
  // with no group is kept: a column whose condition is still being written.
  if (raw.kind === "condition" && typeof raw.name === "string" && raw.name.trim())
    return { kind: "condition", name: raw.name, condition: normalizeCondition(raw.condition) }
  return null
}

const normalizeBanner = (raw: unknown): Banner | null => {
  if (!isRecord(raw)) return null
  const id = str(raw.id)
  if (!id) return null
  const groups = (Array.isArray(raw.groups) ? raw.groups : [])
    .map((g): BannerGroup | null => {
      if (!isRecord(g)) return null
      const gid = str(g.id)
      const variable = str(g.variable)
      const label = optStr(g.label)
      const points = Array.isArray(g.points)
        ? g.points.map(normalizePoint).filter((p): p is BannerPoint => p !== null)
        : null
      if (!gid) return null
      // No variable: a group of conditions, kept only with a heading and
      // only condition columns.
      if (
        !variable &&
        !(label && points && points.length > 0 && points.every((p) => p.kind === "condition"))
      )
        return null
      return {
        id: gid,
        variable,
        ...(variable && g.source === "data" ? { source: "data" as const } : {}),
        ...(variable && typeof g.instance === "string" && g.instance ? { instance: g.instance } : {}),
        ...(label ? { label } : {}),
        points,
      }
    })
    .filter((g): g is BannerGroup => g !== null)
  return { id, name: str(raw.name, "Banner"), groups }
}

const normalizeLabel = (raw: unknown): ShownLabel | null => {
  if (!isRecord(raw)) return null
  const target = labelTargetOf(raw)
  if (!target || typeof raw.shown !== "string" || !raw.shown.trim()) return null
  return { ...target, shown: raw.shown }
}

/**
 * The shown labels of a stored document: entries with a variable, a shown
 * label that is not blank (kept as stored), and at most one of an option and
 * an instance; the first of two entries for the same target. undefined when
 * none is left, so the document then has no `labels` key.
 */
const normalizeLabels = (raw: unknown): ShownLabel[] | undefined => {
  if (!Array.isArray(raw)) return undefined
  const seen = new Set<string>()
  const labels = raw
    .map(normalizeLabel)
    .filter((l): l is ShownLabel => {
      if (l === null) return false
      const key = labelKey(l)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  return labels.length > 0 ? labels : undefined
}

/**
 * Turns a loaded body (a JSON string or a parsed value) into a valid
 * document. A document written by a newer version (above 3) is refused as
 * "newer-version"; anything else that is not a version 1, 2 or 3 document
 * is "invalid". Inside such a document bad fields fall back to defaults
 * (except significance: a defaults record without it means off), tables,
 * banners and shown labels that cannot be read are dropped, and a document
 * with no banner gets one. The result carries the version its contents call
 * for (`schemaVersionFor`). Never throws.
 */
export const normalizeTabSpecDocument = (raw: unknown): NormalizeResult => {
  let value = raw
  if (typeof value === "string") {
    if (!value.trim()) return { ok: false, reason: "invalid" }
    try {
      value = JSON.parse(value)
    } catch {
      return { ok: false, reason: "invalid" }
    }
  }
  if (!isRecord(value)) return { ok: false, reason: "invalid" }
  if (
    value.schemaVersion !== TAB_SPEC_SCHEMA_VERSION &&
    value.schemaVersion !== TAB_SPEC_LABELS_SCHEMA_VERSION &&
    value.schemaVersion !== TAB_SPEC_V3_SCHEMA_VERSION
  ) {
    return {
      ok: false,
      reason:
        typeof value.schemaVersion === "number" &&
        value.schemaVersion > TAB_SPEC_V3_SCHEMA_VERSION
          ? "newer-version"
          : "invalid",
    }
  }
  const banners = (Array.isArray(value.banners) ? value.banners : [])
    .map(normalizeBanner)
    .filter((b): b is Banner => b !== null)
  if (banners.length === 0)
    banners.push({ id: newTabSpecId("banner"), name: "Main banner", groups: [] })

  const hasDefaults = isRecord(value.defaults)
  const d = isRecord(value.defaults) ? value.defaults : {}
  const fallback = createDefaults(banners[0].id)
  const dStats = isRecord(d.statistics) ? d.statistics : {}
  const dScale = isRecord(d.scale) ? d.scale : {}
  const dGrid = isRecord(d.grid) ? d.grid : {}
  const bannerId = banners.some((b) => b.id === d.bannerId)
    ? (d.bannerId as string)
    : banners[0].id
  const defaults: TabSpecDefaults = {
    bannerId,
    base: oneOf(d.base, BASE_RULES, fallback.base),
    // Saving drops an undefined key, so in a saved defaults record a missing
    // significance means off. Only a document with no defaults gets the default.
    significance: hasDefaults
      ? normalizeSignificanceSettings(d.significance)
      : fallback.significance,
    tableStyle: normalizeTableStyleChoice(d.tableStyle) ?? fallback.tableStyle,
    statistics: {
      categorical: Array.isArray(dStats.categorical)
        ? stats(dStats.categorical)
        : fallback.statistics.categorical,
      numeric: Array.isArray(dStats.numeric)
        ? stats(dStats.numeric)
        : fallback.statistics.numeric,
    },
    scale: {
      topBox: box(dScale.topBox, fallback.scale.topBox),
      bottomBox: box(dScale.bottomBox, fallback.scale.bottomBox),
      mean: dScale.mean !== false,
    },
    grid: {
      summary: oneOf(dGrid.summary, ["top-box", "mean", "none"] as const, fallback.grid.summary),
      perRow: dGrid.perRow !== false,
      // The summaries new grids get: the ones that can be read, kept even when none is left.
      ...(Array.isArray(dGrid.summaries)
        ? {
            summaries: dGrid.summaries
              .map(normalizeSummaryChoice)
              .filter((c): c is SummaryChoice => c !== undefined),
          }
        : {}),
    },
    includeHidden: d.includeHidden !== false,
    ...(Array.isArray(d.meanStats) ? { meanStats: meanStatList(d.meanStats) } : {}),
    ...(sortRule(d.sort) ? { sort: sortRule(d.sort) } : {}),
    ...(typeof d.hideEmpty === "boolean" ? { hideEmpty: d.hideEmpty } : {}),
  }

  const seen = new Set<string>()
  const tables = (Array.isArray(value.tables) ? value.tables : [])
    .map(normalizeTable)
    .filter((t): t is TableSpec => {
      if (t === null || seen.has(t.id)) return false
      seen.add(t.id)
      return true
    })
    .map((t) =>
      t.bannerId && !banners.some((b) => b.id === t.bannerId)
        ? // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (({ bannerId: _gone, ...rest }) => rest)(t)
        : t
    )

  const data = isRecord(value.data) ? value.data : {}
  const approval = isRecord(value.approval) ? value.approval : {}
  const labels = normalizeLabels(value.labels)
  // The book filter: no key without a group, as before it existed.
  const read = normalizeCondition(value.filter)
  const filter = read.length > 0 ? read : undefined
  return {
    ok: true,
    document: {
      schemaVersion: schemaVersionFor({ defaults, banners, tables, labels, filter }),
      id: str(value.id) || newTabSpecId("spec"),
      name: str(value.name, "Tab spec"),
      variant: typeof value.variant === "string" ? value.variant : null,
      data: {
        pipelines: strArray(data.pipelines),
        weight: typeof data.weight === "string" && data.weight ? data.weight : null,
      },
      defaults,
      banners,
      tables,
      ...(filter ? { filter } : {}),
      ...(labels ? { labels } : {}),
      approval: {
        state: oneOf(approval.state, ["draft", "in-review", "approved"] as const, "draft"),
        ...(optStr(approval.approvedBy) ? { approvedBy: optStr(approval.approvedBy) } : {}),
        ...(optStr(approval.approvedAt) ? { approvedAt: optStr(approval.approvedAt) } : {}),
        ...(optStr(approval.approvedHash) ? { approvedHash: optStr(approval.approvedHash) } : {}),
      },
      updatedAt: str(value.updatedAt),
      updatedBy: str(value.updatedBy),
    },
  }
}

/**
 * A part of a stored document that reading dropped: a banner or a table
 * (by its 1-based place in the stored list), or a banner's group (by its
 * 1-based place in the banner's stored groups).
 */
export type TabSpecDrop =
  | { kind: "banner"; position: number; name: string }
  | { kind: "group"; banner: string; position: number }
  | { kind: "table"; position: number; id: string }

/**
 * What `normalizeTabSpecDocument` dropped from `raw` (a JSON string or a
 * parsed value) when it read it as `document`: banners, banner groups and
 * tables that could not be read, or that repeat an id. Read lists keep the
 * stored order, so each stored entry is matched to the next read one by id.
 * [] when `raw` is not a document (reading refused it as a whole).
 */
export const droppedOnRead = (raw: unknown, document: TabSpecDocument): TabSpecDrop[] => {
  let value = raw
  if (typeof value === "string") {
    try {
      value = JSON.parse(value)
    } catch {
      return []
    }
  }
  if (!isRecord(value)) return []
  const drops: TabSpecDrop[] = []
  let b = 0
  ;(Array.isArray(value.banners) ? value.banners : []).forEach((stored, i) => {
    const read = document.banners[b]
    if (!read || !isRecord(stored) || str(stored.id) !== read.id) {
      drops.push({ kind: "banner", position: i + 1, name: isRecord(stored) ? str(stored.name) : "" })
      return
    }
    b++
    let g = 0
    ;(Array.isArray(stored.groups) ? stored.groups : []).forEach((group, j) => {
      if (g < read.groups.length && isRecord(group) && str(group.id) === read.groups[g].id) g++
      else drops.push({ kind: "group", banner: read.name, position: j + 1 })
    })
  })
  let t = 0
  ;(Array.isArray(value.tables) ? value.tables : []).forEach((stored, i) => {
    const read = document.tables[t]
    if (read && isRecord(stored) && str(stored.id) === read.id) t++
    else drops.push({ kind: "table", position: i + 1, id: isRecord(stored) ? str(stored.id) : "" })
  })
  return drops
}
